/**
 * FikoGeometricSnap - CATIA/SolidWorks tarzi geometrik snap sistemi
 *
 * Snap Turleri (oncelik sirasiylampa):
 *   1. vertex    - Geometri kose noktasi
 *   2. endpoint  - Kenar uc noktasi (edgeStart/edgeEnd)
 *   3. midpoint  - Kenar orta noktasi (duz veya yay orta noktasi)
 *   4. center    - Daire/silindir merkezi (BREP)
 *   5. quadrant  - Daire ceyrek noktalari (0/90/180/270)
 *   6. tangent   - Teget noktasi (future)
 *   7. edge      - Kenar uzerindeki en yakin nokta (pointOnLine)
 *   8. none      - Snap yok, ham yuzey noktasi
 *
 * Performans:
 *   - Tum vektor hesaplamalari pre-allocated buffer kullanir (0 allocation)
 *   - Screen-space threshold: dunya mesafesi kamera uzakligina gore olceklenir
 *   - Geometry vertex cache: ayni mesh icin tekrar traverse yapmaz
 *   - BREP metadata: FikoCizimYalin.occt_edgeGroups uzerinden erisilir
 */

import * as THREE from 'three';

// ==================== SNAP TYPES ====================

export type SnapType = 'vertex' | 'endpoint' | 'midpoint' | 'center' | 'quadrant' | 'tangent' | 'edge' | 'none';

export interface SnapResult {
  snapped: boolean;
  position: THREE.Vector3;
  type: SnapType;
  distance: number;       // world-space mesafe (snap noktasina)
  /** Snap turune ozel ek bilgi (orn. 'Q0', 'Q90', 'Q180', 'Q270' veya 'ARC_MID') */
  label?: string;
}

export interface SnapConfig {
  enabled: boolean;
  vertexSnap: boolean;
  endpointSnap: boolean;
  midpointSnap: boolean;
  centerSnap: boolean;
  quadrantSnap: boolean;
  edgeSnap: boolean;
  /** Piksel cinsinden snap esigi (screen-space) */
  pixelThreshold: number;
}

// ==================== PRE-ALLOCATED BUFFERS ====================

const _tmpVec3A = new THREE.Vector3();
const _tmpVec3B = new THREE.Vector3();
const _tmpVec3Mid = new THREE.Vector3();
const _tmpVec3C = new THREE.Vector3();
const _tmpVec3D = new THREE.Vector3();
const _tmpVec3Axis = new THREE.Vector3();
const _tmpVec3RefDir = new THREE.Vector3();
const _tmpVec3Q = new THREE.Vector3();

// ==================== DEFAULTS ====================

export const DEFAULT_SNAP_CONFIG: SnapConfig = {
  enabled: true,
  vertexSnap: true,
  endpointSnap: true,
  midpointSnap: true,
  centerSnap: true,
  quadrantSnap: true,
  edgeSnap: true,
  pixelThreshold: 15,
};

// ==================== VERTEX CACHE ====================

interface VertexCacheEntry {
  vertices: Float32Array;  // world-space xyz, interleaved
  count: number;
  timestamp: number;
}

const VERTEX_CACHE = new Map<number, VertexCacheEntry>();
const VERTEX_CACHE_TTL = 5000; // 5 saniye
const VERTEX_CACHE_MAX = 50;

function clearStaleVertexCache(): void {
  const now = performance.now();
  if (VERTEX_CACHE.size <= VERTEX_CACHE_MAX) return;
  for (const [key, entry] of VERTEX_CACHE) {
    if (now - entry.timestamp > VERTEX_CACHE_TTL) {
      VERTEX_CACHE.delete(key);
    }
  }
}

// ==================== BREP EDGE GROUP LOOKUP ====================

/**
 * Intersection nesnesinden BREP edge group bilgisine eris.
 * FikoCizimYalin parent uzerinde occt_edgeGroups olarak saklanir.
 */
function getEdgeGroupFromIntersection(kesisim: any): any | null {
  // edgeId attribute'undan BREP edge index'ini al
  const edgeId = (kesisim as any).edgeId;
  if (edgeId === undefined || edgeId < 0) return null;

  // FikoCizimYalin parent'i (veya occt metadata taşıyan modelleme gövdesi) bul
  let cizim = kesisim.object;
  while (cizim && cizim.type !== 'FikoCizimYalin' && !(cizim as any).occt_edgeGroups) {
    cizim = cizim.parent;
  }
  if (!cizim) return null;

  const edgeGroups = (cizim as any).occt_edgeGroups;
  if (!edgeGroups || !Array.isArray(edgeGroups)) return null;

  // edgeId -> edgeGroups index eslestirmesi
  // edgeGroups array'inde edgeIndex alaniyla eslesir
  for (const group of edgeGroups) {
    if (group.edgeIndex === edgeId) {
      return { group, cizim };
    }
  }

  // Fallback: index olarak dene
  if (edgeId < edgeGroups.length) {
    return { group: edgeGroups[edgeId], cizim };
  }

  return null;
}

/**
 * FikoCizimYalin parent'ini bul (intersection veya mesh'ten)
 */
function findCizimYalinParent(obj: any): any | null {
  let cizim = obj;
  // FikoCizimYalin VEYA occt metadata taşıyan modelleme gövdesi (ölçülebilir nesne)
  while (cizim && cizim.type !== 'FikoCizimYalin' && !cizim.occt_faceGroups && !cizim.occt_edgeGroups) {
    cizim = cizim.parent;
  }
  return cizim || null;
}

// ==================== SNAP INDICATOR MANAGER ====================

/**
 * 3D sahnede snap noktalarini gosteren yardimci sinif.
 * CSS2DRenderer yerine HTML overlay kullanir (daha performansli).
 */
export class FikoSnapIndicator {
  private container: HTMLElement | null = null;
  private markerEl: HTMLDivElement | null = null;
  private labelEl: HTMLDivElement | null = null;
  private visible = false;
  private currentType: SnapType = 'none';

  // Snap turlerine gore renkler ve semboller
  private static readonly SNAP_STYLES: Record<SnapType, { color: string; symbol: string; label: string }> = {
    vertex:   { color: '#FF4444', symbol: '\u25C6', label: 'Kose' },       // diamond
    endpoint: { color: '#44FF44', symbol: '\u25A0', label: 'Uc Nokta' },   // square
    midpoint: { color: '#4488FF', symbol: '\u25B2', label: 'Orta' },       // triangle
    center:   { color: '#FFD700', symbol: '\u2295', label: 'Merkez' },     // circled plus
    quadrant: { color: '#FF8800', symbol: '\u25C7', label: 'Ceyrek' },     // diamond outline
    tangent:  { color: '#FF44FF', symbol: '\u2312', label: 'Teget' },      // arc
    edge:     { color: '#00CCCC', symbol: '\u2022', label: 'Kenar' },      // bullet
    none:     { color: 'transparent', symbol: '', label: '' },
  };

  /**
   * Snap gostergesini baslat (renderer DOM element'ine ekle)
   */
  attach(container: HTMLElement): void {
    if (this.markerEl) return; // Zaten ekli
    this.container = container;

    // Marker element
    this.markerEl = document.createElement('div');
    this.markerEl.style.cssText = `
      position: absolute;
      pointer-events: none;
      z-index: 10000;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      text-shadow: 0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.4);
      transform: translate(-50%, -50%);
      transition: opacity 0.08s ease-out;
      opacity: 0;
    `;

    // Label element (marker altinda)
    this.labelEl = document.createElement('div');
    this.labelEl.style.cssText = `
      position: absolute;
      pointer-events: none;
      z-index: 10000;
      font-size: 10px;
      font-family: 'Segoe UI', Arial, sans-serif;
      font-weight: 600;
      color: white;
      background: rgba(0,0,0,0.75);
      padding: 1px 5px;
      border-radius: 3px;
      white-space: nowrap;
      transform: translate(-50%, 0);
      transition: opacity 0.08s ease-out;
      opacity: 0;
    `;

    container.style.position = container.style.position || 'relative';
    container.appendChild(this.markerEl);
    container.appendChild(this.labelEl);
  }

  /**
   * Snap gostergesini guncelle
   */
  update(
    screenX: number,
    screenY: number,
    snapResult: SnapResult
  ): void {
    if (!this.markerEl || !this.labelEl) return;

    if (!snapResult.snapped || snapResult.type === 'none') {
      this.hide();
      return;
    }

    const style = FikoSnapIndicator.SNAP_STYLES[snapResult.type];

    this.markerEl.textContent = style.symbol;
    this.markerEl.style.color = style.color;
    this.markerEl.style.left = `${screenX}px`;
    this.markerEl.style.top = `${screenY}px`;
    this.markerEl.style.opacity = '1';

    // Label (marker 12px altinda)
    const labelText = snapResult.label || style.label;
    this.labelEl.textContent = labelText;
    this.labelEl.style.left = `${screenX}px`;
    this.labelEl.style.top = `${screenY + 12}px`;
    this.labelEl.style.opacity = '1';
    this.labelEl.style.borderColor = style.color;

    this.visible = true;
    this.currentType = snapResult.type;
  }

  /**
   * Gostergeyi gizle
   */
  hide(): void {
    if (!this.visible) return;
    if (this.markerEl) this.markerEl.style.opacity = '0';
    if (this.labelEl) this.labelEl.style.opacity = '0';
    this.visible = false;
    this.currentType = 'none';
  }

  /**
   * World-space noktayi ekran koordinatina cevir
   */
  static projectToScreen(
    worldPos: THREE.Vector3,
    camera: THREE.Camera,
    width: number,
    height: number
  ): { x: number; y: number } | null {
    _tmpVec3C.copy(worldPos).project(camera);
    // Kamera arkasinda mi?
    if (_tmpVec3C.z > 1) return null;
    return {
      x: (_tmpVec3C.x * 0.5 + 0.5) * width,
      y: (-_tmpVec3C.y * 0.5 + 0.5) * height,
    };
  }

  isVisible(): boolean {
    return this.visible;
  }

  getCurrentType(): SnapType {
    return this.currentType;
  }

  /**
   * Temizle
   */
  dispose(): void {
    if (this.markerEl?.parentNode) {
      this.markerEl.parentNode.removeChild(this.markerEl);
    }
    if (this.labelEl?.parentNode) {
      this.labelEl.parentNode.removeChild(this.labelEl);
    }
    this.markerEl = null;
    this.labelEl = null;
    this.container = null;
    this.visible = false;
  }
}

// ==================== MAIN SNAP CLASS ====================

export class FikoGeometricSnap {
  private config: SnapConfig;
  private _result: SnapResult;

  /** Opsiyonel 3D sahne gostergesi */
  readonly indicator: FikoSnapIndicator = new FikoSnapIndicator();

  constructor(config?: Partial<SnapConfig>) {
    this.config = { ...DEFAULT_SNAP_CONFIG, ...config };
    this._result = {
      snapped: false,
      position: new THREE.Vector3(),
      type: 'none',
      distance: Infinity,
    };
  }

  // ==================== CONFIG ====================

  getConfig(): SnapConfig {
    return this.config;
  }

  updateConfig(partial: Partial<SnapConfig>): void {
    Object.assign(this.config, partial);
  }

  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.config.enabled;
  }

  // ==================== CORE SNAP DETECTION ====================

  /**
   * Ana snap fonksiyonu - intersection objesinden en uygun snap noktasini tespit eder.
   *
   * @param hitPoint - Raycast ham kesisim noktasi (world-space)
   * @param kesisim  - FikoSecililer intersection nesnesi
   * @param camera   - Aktif kamera (screen-space threshold hesabi icin)
   * @param rendererWidth  - Renderer genisligi (piksel)
   * @param rendererHeight - Renderer yuksekligi (piksel)
   * @returns SnapResult - snap durumu, konum, tur ve mesafe
   */
  detect(
    hitPoint: THREE.Vector3,
    kesisim: any,
    camera: THREE.Camera,
    rendererWidth: number,
    rendererHeight: number
  ): SnapResult {
    // Sonucu sifirla (allocation-free)
    this._result.snapped = false;
    this._result.position.copy(hitPoint);
    this._result.type = 'none';
    this._result.distance = Infinity;
    this._result.label = undefined;

    if (!this.config.enabled || !kesisim) return this._result;

    // Screen-space threshold -> world-space threshold donusumu
    const worldThreshold = this.pixelToWorld(
      hitPoint, camera, this.config.pixelThreshold, rendererWidth, rendererHeight
    );

    let bestDist = worldThreshold;

    // BREP edge group bilgisini once al (center, quadrant icin lazim)
    const edgeGroupInfo = getEdgeGroupFromIntersection(kesisim);
    const brepGroup = edgeGroupInfo?.group;
    const cizimParent = edgeGroupInfo?.cizim;

    // 1. VERTEX SNAP (en yuksek oncelik)
    if (this.config.vertexSnap) {
      const vDist = this.findNearestVertex(hitPoint, kesisim, bestDist);
      if (vDist < bestDist) {
        bestDist = vDist;
        this._result.snapped = true;
        this._result.position.copy(_tmpVec3A);
        this._result.type = 'vertex';
        this._result.distance = vDist;
        this._result.label = undefined;
      }
    }

    // 2. ENDPOINT SNAP (kenar uc noktalari)
    if (this.config.endpointSnap && kesisim.edgeStart && kesisim.edgeEnd) {
      const eDist = this.findNearestEndpoint(hitPoint, kesisim.edgeStart, kesisim.edgeEnd, bestDist);
      if (eDist < bestDist) {
        bestDist = eDist;
        this._result.snapped = true;
        this._result.position.copy(_tmpVec3A);
        this._result.type = 'endpoint';
        this._result.distance = eDist;
        this._result.label = undefined;
      }
    }

    // 3. MIDPOINT SNAP (kenar orta noktasi)
    if (this.config.midpointSnap && kesisim.edgeStart && kesisim.edgeEnd) {
      // Egrisel kenar ise yay orta noktasini hesapla, duz kenar ise linear midpoint
      const isCurve = !!(kesisim as any).isCurveEdge;
      let mDist: number;
      let midLabel: string | undefined;

      if (isCurve && brepGroup && brepGroup.curveType === 'CIRCLE' && brepGroup.center && brepGroup.radius > 0) {
        mDist = this.findArcMidpoint(hitPoint, kesisim.edgeStart, kesisim.edgeEnd, brepGroup, cizimParent, bestDist);
        midLabel = 'Yay Orta';
      } else {
        mDist = this.findMidpoint(hitPoint, kesisim.edgeStart, kesisim.edgeEnd, bestDist);
        midLabel = undefined;
      }

      if (mDist < bestDist) {
        bestDist = mDist;
        this._result.snapped = true;
        this._result.position.copy(_tmpVec3Mid);
        this._result.type = 'midpoint';
        this._result.distance = mDist;
        this._result.label = midLabel;
      }
    }

    // 4. CENTER SNAP (daire/silindir merkezi - BREP metadatasindan)
    if (this.config.centerSnap) {
      const cDist = this.findCenterBREP(hitPoint, kesisim, brepGroup, cizimParent, bestDist);
      if (cDist < bestDist) {
        bestDist = cDist;
        this._result.snapped = true;
        this._result.position.copy(_tmpVec3A);
        this._result.type = 'center';
        this._result.distance = cDist;
        this._result.label = undefined;
      }
    }

    // 5. QUADRANT SNAP (daire ceyrek noktalari: 0/90/180/270)
    if (this.config.quadrantSnap && brepGroup &&
        (brepGroup.curveType === 'CIRCLE' || brepGroup.curveType === 'ELLIPSE') &&
        brepGroup.center && brepGroup.radius > 0) {
      const qResult = this.findQuadrantPoints(hitPoint, brepGroup, cizimParent, bestDist);
      if (qResult.dist < bestDist) {
        bestDist = qResult.dist;
        this._result.snapped = true;
        this._result.position.copy(_tmpVec3Q);
        this._result.type = 'quadrant';
        this._result.distance = qResult.dist;
        this._result.label = qResult.label;
      }
    }

    // 6. EDGE SNAP (kenar uzerindeki en yakin nokta)
    if (this.config.edgeSnap && kesisim.pointOnLine) {
      const pDist = hitPoint.distanceTo(kesisim.pointOnLine);
      if (pDist < bestDist) {
        bestDist = pDist;
        this._result.snapped = true;
        this._result.position.copy(kesisim.pointOnLine);
        this._result.type = 'edge';
        this._result.distance = pDist;
        this._result.label = undefined;
      }
    }

    return this._result;
  }

  // ==================== SNAP DETECTORS ====================

  /**
   * En yakin vertex'i bul (geometry buffer traverse)
   * Sonucu _tmpVec3A'ya yazar.
   */
  private findNearestVertex(
    hitPoint: THREE.Vector3,
    kesisim: any,
    threshold: number
  ): number {
    const mesh = kesisim.altObje || kesisim.object;
    if (!mesh || !(mesh instanceof THREE.Mesh || mesh instanceof THREE.LineSegments)) {
      return Infinity;
    }

    const geometry = mesh.geometry as THREE.BufferGeometry;
    if (!geometry?.attributes?.position) return Infinity;

    const positions = geometry.attributes.position;
    const worldMatrix = mesh.matrixWorld;
    const meshId = mesh.id;

    // Cache kontrolu
    let cached = VERTEX_CACHE.get(meshId);
    const now = performance.now();

    if (!cached || now - cached.timestamp > VERTEX_CACHE_TTL) {
      // Cache'e world-space vertex'leri yaz
      const count = positions.count;
      const worldVerts = new Float32Array(count * 3);

      for (let i = 0; i < count; i++) {
        _tmpVec3B.fromBufferAttribute(positions, i);
        _tmpVec3B.applyMatrix4(worldMatrix);
        worldVerts[i * 3] = _tmpVec3B.x;
        worldVerts[i * 3 + 1] = _tmpVec3B.y;
        worldVerts[i * 3 + 2] = _tmpVec3B.z;
      }

      cached = { vertices: worldVerts, count, timestamp: now };
      VERTEX_CACHE.set(meshId, cached);
      clearStaleVertexCache();
    }

    // En yakin vertex'i bul
    let bestDist = threshold;
    let bestIdx = -1;
    const verts = cached.vertices;
    const hx = hitPoint.x, hy = hitPoint.y, hz = hitPoint.z;

    for (let i = 0; i < cached.count; i++) {
      const ox = i * 3;
      const dx = verts[ox] - hx;
      const dy = verts[ox + 1] - hy;
      const dz = verts[ox + 2] - hz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = ox;
      }
    }

    if (bestIdx >= 0) {
      _tmpVec3A.set(verts[bestIdx], verts[bestIdx + 1], verts[bestIdx + 2]);
      return bestDist;
    }
    return Infinity;
  }

  /**
   * En yakin kenar uc noktasini bul
   * Sonucu _tmpVec3A'ya yazar.
   */
  private findNearestEndpoint(
    hitPoint: THREE.Vector3,
    edgeStart: THREE.Vector3,
    edgeEnd: THREE.Vector3,
    threshold: number
  ): number {
    const dStart = hitPoint.distanceTo(edgeStart);
    const dEnd = hitPoint.distanceTo(edgeEnd);
    const minDist = Math.min(dStart, dEnd);

    if (minDist < threshold) {
      _tmpVec3A.copy(dStart <= dEnd ? edgeStart : edgeEnd);
      return minDist;
    }
    return Infinity;
  }

  /**
   * Duz kenar orta noktasini bul
   * Sonucu _tmpVec3Mid'e yazar.
   */
  private findMidpoint(
    hitPoint: THREE.Vector3,
    edgeStart: THREE.Vector3,
    edgeEnd: THREE.Vector3,
    threshold: number
  ): number {
    _tmpVec3Mid.addVectors(edgeStart, edgeEnd).multiplyScalar(0.5);
    const dist = hitPoint.distanceTo(_tmpVec3Mid);
    return dist < threshold ? dist : Infinity;
  }

  /**
   * Egrisel kenar (yay) orta noktasini bul.
   * Dairenin merkezi ve radius bilgisinden yay orta noktasini hesaplar.
   * Sonucu _tmpVec3Mid'e yazar.
   */
  private findArcMidpoint(
    hitPoint: THREE.Vector3,
    edgeStart: THREE.Vector3,
    edgeEnd: THREE.Vector3,
    brepGroup: any,
    cizimParent: any,
    threshold: number
  ): number {
    const center = brepGroup.center;
    const radius = brepGroup.radius;
    if (!center || !radius) return Infinity;

    // Merkez noktasini world-space'e cevir
    _tmpVec3C.set(center.x, center.y, center.z);
    if (cizimParent?.matrixWorld) {
      _tmpVec3C.applyMatrix4(cizimParent.matrixWorld);
    }

    // edgeStart ve edgeEnd'in merkeze gore yonlerini al
    _tmpVec3A.copy(edgeStart).sub(_tmpVec3C).normalize();
    _tmpVec3B.copy(edgeEnd).sub(_tmpVec3C).normalize();

    // Yay orta noktasi: iki yonun ortalamasinin normalize edilmis hali * radius + merkez
    _tmpVec3Mid.addVectors(_tmpVec3A, _tmpVec3B);

    const midLen = _tmpVec3Mid.length();
    if (midLen < 0.0001) {
      // Yari daire - iki yon tam zit, perpendicular yon gerekli
      // Axis bilgisi varsa kullan
      if (brepGroup.axis) {
        _tmpVec3Axis.set(brepGroup.axis.x, brepGroup.axis.y, brepGroup.axis.z);
        if (cizimParent?.matrixWorld) {
          // Axis'i sadece rotasyon ile donustur (position etkilemesin)
          _tmpVec3Axis.transformDirection(cizimParent.matrixWorld);
        }
        _tmpVec3Mid.crossVectors(_tmpVec3A, _tmpVec3Axis).normalize();
      } else {
        // Fallback: linear midpoint
        _tmpVec3Mid.addVectors(edgeStart, edgeEnd).multiplyScalar(0.5);
        const dist = hitPoint.distanceTo(_tmpVec3Mid);
        return dist < threshold ? dist : Infinity;
      }
    } else {
      _tmpVec3Mid.normalize();
    }

    // World radius hesapla (scale etkilenebilir)
    const worldRadius = _tmpVec3C.distanceTo(edgeStart);

    // Yay orta noktasi
    _tmpVec3Mid.multiplyScalar(worldRadius).add(_tmpVec3C);

    const dist = hitPoint.distanceTo(_tmpVec3Mid);
    return dist < threshold ? dist : Infinity;
  }

  /**
   * Daire/silindir merkezi bul (BREP metadata - duzeltilmis versiyon)
   * Sonucu _tmpVec3A'ya yazar.
   *
   * Onceki bug: mesh.geometry?.userData?.occt_edgeGroups araniyordu
   * ama veri FikoCizimYalin parent uzerinde.
   */
  private findCenterBREP(
    hitPoint: THREE.Vector3,
    kesisim: any,
    brepGroup: any,
    cizimParent: any,
    threshold: number
  ): number {
    // 1. Dogrudan BREP edge group'tan merkez al (en dogru yontem)
    if (brepGroup && brepGroup.center &&
        (brepGroup.curveType === 'CIRCLE' || brepGroup.curveType === 'ELLIPSE')) {
      _tmpVec3A.set(brepGroup.center.x, brepGroup.center.y, brepGroup.center.z);
      if (cizimParent?.matrixWorld) {
        _tmpVec3A.applyMatrix4(cizimParent.matrixWorld);
      }
      const dist = hitPoint.distanceTo(_tmpVec3A);
      if (dist < threshold) return dist;
    }

    // 2. FikoCizimYalin uzerindeki tum edgeGroups'tan CIRCLE merkezi ara
    const obj = kesisim.altObje || kesisim.object;
    const cizim = findCizimYalinParent(obj);
    if (cizim) {
      const allEdgeGroups = (cizim as any).occt_edgeGroups;
      if (allEdgeGroups && Array.isArray(allEdgeGroups)) {
        let bestCenterDist = threshold;
        for (const group of allEdgeGroups) {
          if ((group.curveType === 'CIRCLE' || group.curveType === 'ELLIPSE') && group.center) {
            _tmpVec3B.set(group.center.x, group.center.y, group.center.z);
            if (cizim.matrixWorld) {
              _tmpVec3B.applyMatrix4(cizim.matrixWorld);
            }
            const d = hitPoint.distanceTo(_tmpVec3B);
            if (d < bestCenterDist) {
              bestCenterDist = d;
              _tmpVec3A.copy(_tmpVec3B);
            }
          }
        }
        if (bestCenterDist < threshold) return bestCenterDist;
      }

      // 3. Face metadata'dan CYLINDER/SPHERE merkezi
      const faceGroups = (cizim as any).occt_faceGroups;
      if (faceGroups && Array.isArray(faceGroups)) {
        let bestFaceDist = threshold;
        for (const group of faceGroups) {
          if ((group.surfaceType === 'CYLINDER' || group.surfaceType === 'SPHERE') &&
              group.surfaceData?.center) {
            const c = group.surfaceData.center;
            _tmpVec3B.set(c.x, c.y, c.z);
            if (cizim.matrixWorld) {
              _tmpVec3B.applyMatrix4(cizim.matrixWorld);
            }
            const d = hitPoint.distanceTo(_tmpVec3B);
            if (d < bestFaceDist) {
              bestFaceDist = d;
              _tmpVec3A.copy(_tmpVec3B);
            }
          }
        }
        if (bestFaceDist < threshold) return bestFaceDist;
      }
    }

    return Infinity;
  }

  /**
   * Daire ceyrek noktalarini bul (0, 90, 180, 270 derece)
   * BREP edge group'tan merkez, radius ve axis bilgisi kullanilir.
   * Sonucu _tmpVec3Q'ya yazar.
   * @returns { dist, label } - en yakin ceyrek noktasinin mesafesi ve etiketi
   */
  private findQuadrantPoints(
    hitPoint: THREE.Vector3,
    brepGroup: any,
    cizimParent: any,
    threshold: number
  ): { dist: number; label: string } {
    const center = brepGroup.center;
    const radius = brepGroup.radius;
    const axis = brepGroup.axis;
    if (!center || !radius) return { dist: Infinity, label: '' };

    // Merkez ve axis'i world-space'e cevir
    _tmpVec3C.set(center.x, center.y, center.z);

    // Dairenin normal ekseni
    if (axis) {
      _tmpVec3Axis.set(axis.x, axis.y, axis.z);
    } else {
      _tmpVec3Axis.set(0, 0, 1); // Varsayilan Z ekseni
    }

    if (cizimParent?.matrixWorld) {
      _tmpVec3C.applyMatrix4(cizimParent.matrixWorld);
      _tmpVec3Axis.transformDirection(cizimParent.matrixWorld);
    }
    _tmpVec3Axis.normalize();

    // Daire duzleminde iki ortogonal yon olustur
    // Axis'e dik bir referans yonu bul
    if (Math.abs(_tmpVec3Axis.y) < 0.99) {
      _tmpVec3RefDir.set(0, 1, 0);
    } else {
      _tmpVec3RefDir.set(1, 0, 0);
    }
    // U = normalize(refDir cross axis)
    _tmpVec3D.crossVectors(_tmpVec3RefDir, _tmpVec3Axis).normalize();
    // V = normalize(axis cross U)
    _tmpVec3RefDir.crossVectors(_tmpVec3Axis, _tmpVec3D).normalize();

    // World radius (scale etkilenebilir)
    const worldRadius = radius; // TODO: scale factor varsa uygula

    // 4 ceyrek nokta: 0, 90, 180, 270 derece
    const quadLabels = ['Q0\u00B0', 'Q90\u00B0', 'Q180\u00B0', 'Q270\u00B0'];
    const angles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];

    let bestDist = threshold;
    let bestLabel = '';

    for (let i = 0; i < 4; i++) {
      const cos = Math.cos(angles[i]);
      const sin = Math.sin(angles[i]);

      // Q = center + radius * (cos * U + sin * V)
      _tmpVec3B.copy(_tmpVec3D).multiplyScalar(cos * worldRadius);
      _tmpVec3A.copy(_tmpVec3RefDir).multiplyScalar(sin * worldRadius);
      _tmpVec3B.add(_tmpVec3A).add(_tmpVec3C);

      const dist = hitPoint.distanceTo(_tmpVec3B);
      if (dist < bestDist) {
        bestDist = dist;
        _tmpVec3Q.copy(_tmpVec3B);
        bestLabel = quadLabels[i];
      }
    }

    return { dist: bestDist, label: bestLabel };
  }

  // ==================== SCREEN-SPACE UTILS ====================

  /**
   * Piksel mesafesini world-space mesafesine cevir
   * Kamera uzakligina gore dogrusal olcekleme
   */
  private pixelToWorld(
    worldPoint: THREE.Vector3,
    camera: THREE.Camera,
    pixelDistance: number,
    rendererWidth: number,
    rendererHeight: number
  ): number {
    if (camera instanceof THREE.PerspectiveCamera) {
      const cameraDistance = camera.position.distanceTo(worldPoint);
      const fovRad = (camera.fov * Math.PI) / 180;
      const worldPerPixel = (2 * cameraDistance * Math.tan(fovRad / 2)) / rendererHeight;
      return pixelDistance * worldPerPixel;
    }
    // Orthographic kamera
    if (camera instanceof THREE.OrthographicCamera) {
      const worldPerPixel = (camera.top - camera.bottom) / rendererHeight;
      return pixelDistance * worldPerPixel;
    }
    return pixelDistance * 0.01; // fallback
  }

  // ==================== DISPOSE ====================

  dispose(): void {
    VERTEX_CACHE.clear();
    this.indicator.dispose();
  }
}
